import { Container } from 'pixi.js';
import type { LevelTaskProgress } from '../types';
import { LevelTaskType } from '../types';
import type { LevelTasksViewGroup, TaskViewLayout } from './types';
import { ProgressTaskView } from './ProgressTaskView';
import { TaskView } from './TaskView';
import { BasicTaskLayout } from '../../../../layouts/taskViews/BasicTaskLayout';
import { ProgressTaskLayout } from '../../../../layouts/taskViews/ProgressTaskLayout';

interface ConstructTaskViewOptions {
  readonly forceBasic?: boolean;
  readonly constructLayout?: () => TaskViewLayout;
  readonly taskPaddingX?: number;
}

export interface CreateTasksViewGroupOptions extends ConstructTaskViewOptions {
  readonly incompleteOnly?: boolean;
}

const constructorMap: Record<
  LevelTaskType,
  (progress: LevelTaskProgress, options: ConstructTaskViewOptions) => TaskView
> = {
  [LevelTaskType.Hexes]: (progress, { forceBasic, constructLayout }) =>
    !forceBasic ? createProgressTaskView(progress, 'icon') : createBasicTaskView(progress, 'icon', constructLayout?.()),
  [LevelTaskType.PlankBlockers]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Small_Plank_Icon', constructLayout?.()),
  [LevelTaskType.TapeBlockers]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Small_Tape_Icon', constructLayout?.()),
  [LevelTaskType.KnotBlockers]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Small_Knot_Icon', constructLayout?.()),
  [LevelTaskType.Birds]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Small_Bird_Icon', constructLayout?.()),
  [LevelTaskType.Stars]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Goal_Star_Icon', constructLayout?.()),
  [LevelTaskType.Rocket]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Small_Rocket_Icon', constructLayout?.()),
  [LevelTaskType.MeasureTape]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'small_measure_tape_icon', constructLayout?.()),
  [LevelTaskType.PlankRed]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Red', constructLayout?.()),
  [LevelTaskType.PlankGreen]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Green', constructLayout?.()),
  [LevelTaskType.PlankBlue]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Blue', constructLayout?.()),
  [LevelTaskType.PlankPink]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Pink', constructLayout?.()),
  [LevelTaskType.PlankYellow]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Yellow', constructLayout?.()),
  [LevelTaskType.PlankGrey]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Grey', constructLayout?.()),
  [LevelTaskType.PlankWhite]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_White', constructLayout?.()),
  [LevelTaskType.PlankNavy]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Navy', constructLayout?.()),
  [LevelTaskType.PlankPurple]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Purple', constructLayout?.()),
  [LevelTaskType.PlankAny]: (progress, { constructLayout }) =>
    createBasicTaskView(progress, 'Plank_Small_Icon_Any', constructLayout?.()),
};

export function createTasksViewGroup(
  tasks: LevelTaskProgress[],
  { forceBasic = false, incompleteOnly = true, taskPaddingX = 5, constructLayout }: CreateTasksViewGroupOptions = {},
): LevelTasksViewGroup {
  const container = new Container();
  const innerContainer = new Container().props({ parent: container });
  const viewMap = new Map<LevelTaskType, TaskView>();
  const visibleTasks = incompleteOnly ? tasks.filter((task) => task.collected < task.goal) : tasks.slice();

  const views = visibleTasks.map(({ goal = 0, collected = 0, type: taskType }) => {
    const taskView = constructorMap[taskType](
      {
        type: taskType,
        collected,
        goal,
      },
      {
        // if there are more than 2 visibleTasks all tasks will use basic layout.
        forceBasic: forceBasic || visibleTasks.length > 2,
        constructLayout,
      },
    );

    viewMap.set(taskType, taskView);
    innerContainer.addChild(taskView);

    // Adjust progressbar size when multiple items.
    if (taskView instanceof ProgressTaskView && tasks.length > 1) {
      taskView.progressBarWidth *= 0.74;
    }

    // Collapse tasks once completed.
    taskView.onGoalAnimationComplete.connect(() => {
      if (incompleteOnly && taskView.isTaskVisuallyComplete()) {
        arrangeTaskViews(innerContainer, views, true, incompleteOnly, taskPaddingX);
      }
    });

    return taskView;
  });

  arrangeTaskViews(innerContainer, views, false, incompleteOnly, taskPaddingX);

  const allTasksVisuallyComplete = (): boolean => views.every((taskView) => taskView.isTaskVisuallyComplete());

  return {
    container,
    views: viewMap,
    allTasksVisuallyComplete,
  };
}

function arrangeTaskViews(
  innerContainer: Container,
  taskViews: TaskView[],
  animate: boolean,
  hideComplete: boolean,
  taskPaddingX: number,
): void {
  let xPos = 0;

  for (let i = 0; i < taskViews.length; i += 1) {
    const taskView = taskViews[i];
    taskView.removeTweens();

    if (hideComplete && taskView.isTaskVisuallyComplete()) {
      taskView.tweenTo({ alpha: 0 }, 250, 'quadOut', true, 100);
      continue;
    }

    const taskX = xPos - taskView.getLocalBounds().minX;

    if (!animate) {
      taskView.x = taskX;
    } else {
      taskView.tweenTo({ x: taskX }, 400, 'quadOut', true, 350);
    }

    xPos += taskView.width + (i < taskViews.length - 1 ? taskPaddingX : 0);
  }

  const innerContainerX = -xPos / 2;

  if (!animate) {
    innerContainer.x = innerContainerX;
  } else {
    innerContainer.tweenTo({ x: innerContainerX }, 400, 'linear', true, 350);
  }
}

function createBasicTaskView(progress: LevelTaskProgress, iconFrame: string, layout?: TaskViewLayout): TaskView {
  return new TaskView({
    layout: layout ?? new BasicTaskLayout(),
    progress,
    iconFrame,
  });
}

function createProgressTaskView(progress: LevelTaskProgress, iconFrame: string): ProgressTaskView {
  return new ProgressTaskView({
    layout: new ProgressTaskLayout(),
    progress,
    iconFrame,
  });
}
